import { pageTypes } from 'config/pageTypes';
import largeStorageHero from 'assets/images/sgp/SGPHero/large-self-storage-hero.webp';
import storage10x20 from 'assets/images/sgp/SizeGuide/10x20.svg';
import storage10x25 from 'assets/images/sgp/SizeGuide/10x25.svg';
import storage10x30 from 'assets/images/sgp/SizeGuide/10x30.svg';
// Shared assets: Using medium storage images as they are same
import headerImage from 'assets/images/sgp/MediumStorage/10x30-Unit.webp';

// Shared assets: Using medium-size related content images that are same
import relatedContentImage1 from 'assets/images/sgp/featuredContentImages/mediumSizeRelatedContentImage1.webp';
import relatedContentImage2 from 'assets/images/sgp/featuredContentImages/mediumSizeRelatedContentImage2.webp';
import relatedContentImage3 from 'assets/images/sgp/featuredContentImages/sharing-unit-with-friend.webp';
import relatedContentImage4 from 'assets/images/sgp/featuredContentImages/mediumSizeRelatedContentImage4.webp';
import { linkToOtherPSPsBlock } from './shared';

const heroContent = {
	title: `Find Affordable Large Self Storage Units Near You`,
	description: `There are moments in life when you need more space. Large self storage units are here to answer the call. They allow you to store what you need to, protect your memories, and keep you organized through it all. From whole-home moves to [sprawling DIY projects](https://www.selfstorage.com/blog/category/diy-move/), a big self storage unit gives you the capacity to thrive.`,
	image: largeStorageHero,
	storageType: 'Large Self Storage',
	subheading: 'Find Budget Friendly Large Self Storage',
	CTAtext: 'Find Storage',
	searchPlaceholder: 'Enter ZIP Code or City Name',
};

const videoContent = {
	title: 'What Are Large Self Storage Units?',
	background: 'blueWhiteGradient',
	modules: [
		{
			type: 'video',
			props: {
				pageType: pageTypes.SGP,
				content: {
					subtitle: 'When More Space Is the Right Call',
					title: 'What Are Large Self Storage Units?',
					description: `Large self storage units typically range from 10x20 to 10x30 feet, covering 200 to 300 square feet of storage space. These units are the go-to choice for whole-home moves, major renovation projects, [vehicle self storage](https://www.selfstorage.com/storage-types/vehicle-self-storage), and businesses that need serious room for inventory, equipment, or supplies.`,
					videoSrc: 'https://www.youtube.com/embed/Wzs57ICy5VY',
				},
			},
		},
	],
};

const sizeChartContent = [
	{
		type: 'cardStrip',
		props: {
			heading: 'Large Self Storage Unit Size Guide',
			pageType: pageTypes.SGP,
			description:
				"Not sure which size is right for your situation? Here's a breakdown of what each big self storage unit option can hold:",
			cardData: [
				{
					title: '10x20',
					size: '200 ft² | 1,600 ft³',
					content:
						'**Best For:**\n\n- Contents of a 3-bedroom home\n- Full furniture sets, large appliances, and moving boxes\n- [Compact vehicles including motorcycles](https://www.selfstorage.com/blog/small-vehicle-storage-guide/) and small boats\n- Large business inventory and equipment',
					imageSrc: storage10x20,
					contentAlign: 'text-left sgp',
				},
				{
					title: '10x25',
					size: '250 ft² | 2,000 ft³',
					content:
						'**Best For:**\n\n- Contents of a 4-bedroom home\n- Multiple full furniture sets and appliances\n- Midsized vehicles, including some basic trucks, trailers, and watercraft\n- Contractor equipment and commercial inventory',
					imageSrc: storage10x25,
					contentAlign: 'text-left sgp',
				},
				{
					title: '10x30',
					size: '300 ft² | 2,400 ft³',
					content:
						'**Best For:**\n\n- Contents of a large or multi-story home\n- Full vehicle storage alongside household goods\n- [Long-term storage for major renovation](https://www.selfstorage.com/blog/renovation-vs-remodel-guide/) or relocation projects\n- Large-scale business and commercial storage needs',
					imageSrc: storage10x30,
					contentAlign: 'text-left sgp',
				},
			],
		},
	},
];

const pricingContent = [
	{
		type: 'sizeComparisonSection',
		props: {
			content: {
				title: 'Large Self Storage Unit Pricing Overview',
				description:
					"Large self storage units are a bigger investment, but the right unit at the right price is easier to find than you might expect. Here's a pricing snapshot from our self storage network to help you plan.\n\nPrices vary based on location, amenities, and seasonal demand. Use our self storage comparison tool to shop and compare options in your area.",
				image: '',
				contentdec: '',
			},
			storageData: [
				{
					size: '10x20',
					heading: "10' Wide x 20' Long",
					desc: [
						'**Monthly Average:** $127.93\n\n**Unit + Climate Control:** Expect a 10–20% increase in monthly cost',
					],
				},
				{
					size: '10x25',
					heading: "10' Wide x 25' Long",
					desc: [
						'**Monthly Average:** $146.89\n\n**Unit + Climate Control:** Expect a 10–20% increase in monthly cost',
					],
				},
				{
					size: '10x30',
					heading: "10' Wide x 30' Long",
					desc: [
						'**Monthly Average:** $169.33\n\n**Unit + Climate Control:** Expect a 10–20% increase in monthly cost',
					],
				},
			],
		},
	},
];

const choosingRightUnitContent = [
	{
		type: 'makeMostOf',
		props: {
			heading: 'How to Choose the Right Large Self Storage Unit',
			description:
				"Choosing the right big self storage unit comes down to knowing what you're working with. A little measuring upfront saves you from renting too small — or paying for more space than you need. Here's how to approach it:",
			columns: [
				{
					subHeading: 'Start with your largest items first',
					items: [
						"The biggest pieces in your haul — typically large furniture, appliances, or vehicles — will determine your minimum unit size. Measure each item's length, width, and height, and factor in that items will often be stacked or stored on their sides to maximize space.",
					],
				},
				{
					subHeading: 'Add a buffer',
					items: [
						"Once you've totaled up your square footage, build in some extra room. A unit that's packed wall-to-wall is difficult to navigate and makes retrieving items a hassle. A general rule of thumb: aim for about 20–30% more space than your measurements suggest you need.",
					],
				},
				{
					subHeading: 'Account for vertical space',
					items: [
						"Large self storage units typically offer 8–10 feet of ceiling height. Stacking boxes and using shelving units can effectively double your usable storage area — so don't just think in square feet, think in cubic feet too.",
					],
				},
			],
		},
	},
];

const realLifeMeasuringContent = [
	{
		type: 'content',
		props: {
			title: 'Real-Life Measuring Examples',
			titleTag: 'h3',
			theme: 'white',
			contentType: 'row-type',
			content: [
				'#### **King-Size Bed Frame and Mattress**\n\n[A standard king mattress](https://www.selfstorage.com/blog/how-to-store-a-mattress-in-self-storage/) measures approximately 76" wide x 80" long. Stored flat, it takes up roughly 42 square feet of floor space. Stored on its side against a wall, it occupies only about 7 square feet of floor space — freeing up significant room for other items. Always measure your frame separately, as headboards and footboards vary widely in size.',
				"#### **Dining Room Set**\n\nDining tables vary quite a bit in size, but most standard rectangular tables land somewhere between 36–42\" wide and 60–84\" long. That puts the table alone at roughly 15–25 square feet of floor space — and that's before you factor in chairs. Six chairs at an average of 18\" x 20\" each add another 15 square feet or so, depending on how they're arranged. Stacking or nesting chairs reduces that footprint considerably, so it's worth doing that where you can. If your table has extension leaves, measure it fully extended — that's the dimension that matters when you're figuring out whether it'll fit through the unit door.",
				"#### **Kids' Bedroom Furniture**\n\nA typical children's bedroom setup — a twin or full bed frame, dresser, nightstand, and small bookcase — will take up somewhere in the range of 28–40 square feet of floor space, though that number shifts depending on the size and style of each piece. Bunk beds in particular are worth measuring assembled before you take them apart, since the frame footprint is what you're really working around. And it's easy to underestimate the extras: mattresses, storage bins, a small desk, a toy chest. Do a full room inventory before you settle on a unit size — kids' rooms tend to have more in them than they appear to.",
			],
		},
	},
];

const howToMeasureContent = [
	{
		type: 'content',
		props: {
			title: 'How to Measure for Self Storage',
			description:
				"Choosing the right big self storage unit comes down to knowing what you're working with. A little measuring upfront saves you from renting too small — or paying for more space than you need. Here's how to approach it:",
			theme: 'dark',
			contentType: 'column-type',
			content: [
				"**Start with your largest items first.** \n\n - The biggest pieces in your haul — typically large furniture, appliances, or vehicles — will determine your minimum unit size. Measure each item's length, width, and height, and factor in that items will often be stacked or stored on their sides to maximize space.",
				"**Add a buffer.**\n\n - Once you've totaled up your square footage, build in some extra room. A unit that's packed wall-to-wall is difficult to navigate and makes retrieving items a hassle. A general rule of thumb: aim for about 20–30% more space than your measurements suggest you need.",
				"**Account for vertical space.**\n\n - Large self storage units typically offer 8–10 feet of ceiling height. Stacking boxes and using shelving units can effectively double your usable storage area — so don't just think in square feet, think in cubic feet too.",
			],
		},
	},
];

const reviewsContent = {
	headerTitle: '',
	reviews: [
		{
			id: 'large-self-storage-review-1',
			title: 'Great service',
			stars: 5,
			text: 'Worked with Jackie on 8/6/25. Great and knowledgeable service. I was able to rent a 30x12 spot for my RV and a 10x20 for my trailer. Excellent price, no complaints. Thank you.',
			customerName: 'Mario Felder',
			date: '2025-09-06',
			link: 'https://www.trustpilot.com/reviews/68bc686eb8b9f436b0b67eb4',
		},
		{
			id: 'large-self-storage-review-2',
			title: 'Best customer service for storage units!',
			stars: 5,
			text: "Self storage was very helpful! They made sure to get me the best deal in the area. 10x20 climate controlled unit for 185$ online! The nice man I spoke with ( forgot his name) went above and beyond! I was really impressed with how much care and consideration he put forth to assist me. Other storage places wanted 220$ plus to get the same 10x20 I was needing. Thanks self storage! I'll be telling everyone I know of my great encounter with the nice guy that took my call.!",
			customerName: 'April Pless',
			date: '2024-05-16',
			link: 'https://www.trustpilot.com/reviews/664621f95fc380662a017725',
		},
		{
			id: 'large-self-storage-review-3',
			title: 'Reserving my unit 10x20…',
			stars: 5,
			text: 'Reserving my unit 10x20 was such a pleasant experience. It was so easy. I just scrolled through the web page until I found the unit I needed and filled out the easy and short form, and hit the ok. It was done, and no credit or debit card needed to reserve. Amazing!! Then I got a confirmation email with complete instructions. wait, then i got a call from them, making sure everything went well with my order. They are absolutely GREAT. I recommend them.',
			customerName: 'William Hanner',
			date: '2023-09-08',
			link: 'https://www.trustpilot.com/reviews/64fb6ed3aca74c895f2b2b5f',
		},
		{
			id: 'large-self-storage-review-4',
			title: 'Affordable rates, friendly service, clean units!',
			stars: 5,
			text: "I'm really impressed with the quality of the storage! The manager is friendly and funny and resolved all of my concerns and answered all of my questions. We even got a free lock for setting up a reservation online! This is also by far the best price I have found for a 10x30 storage unit in the area, better by $40 / month than the competition!",
			customerName: 'Henry Paul',
			date: '2016-06-24',
			link: 'https://www.trustpilot.com/reviews/576cb11c0000ff000969415d',
		},
	],
};

const relatedContent = {
	heading: 'Related Resources',
	type: 'unitCards',
	id: 'large-storage-related-content',
	data: {
		slug: 'large-storage-related-content',
		unitCards: [
			{
				title: 'Storage',
				url: 'https://www.selfstorage.com/blog/self-storage-unit-size-guide-2/',
				layout: false,
				plain: true,
				date: '',
				image: relatedContentImage1,
				content:
					'Self Storage Size Guide: Finding the Perfect Storage Unit Size',
			},
			{
				title: 'Storage',
				url: 'https://www.selfstorage.com/blog/storage-unit-prices/',
				layout: false,
				plain: true,
				date: '',
				image: relatedContentImage2,
				content: 'Storage Unit Prices: How Much Is a Storage Unit?',
			},
			{
				title: 'Storage',
				url: 'https://www.selfstorage.com/blog/sharing-a-storage-unit/',
				layout: false,
				plain: true,
				date: '',
				image: relatedContentImage3,
				content:
					'Sharing a Storage Unit with a Friend: The Pros and Cons Revealed',
			},
			{
				title: 'Storage',
				url: 'https://www.selfstorage.com/blog/how-storage-reduces-cost-of-living/',
				layout: false,
				plain: true,
				date: '',
				image: relatedContentImage4,
				content:
					'The Benefits of Downsizing Your Home: How to Reduce the Cost of Living with Self Storage',
			},
		],
	},
};

export const LargeSelfStorageContent = {
	type: 'large-self-storage',
	pageType: pageTypes.SGP,
	reviews: reviewsContent.reviews,
	sections: [
		{
			title: 'Large Self Storage Hero',
			background: 'default',
			pageType: pageTypes.SGP,
			modules: [
				{
					type: 'hero',
					id: 'large-self-storage-hero',
					props: {
						content: heroContent,
						pageType: pageTypes.SGP,
						additionalProps: {
							heading: {
								size: 'large',
								color: 'white',
								weight: 'ultraBold',
							},
						},
					},
				},
			],
		},
		videoContent,
		{
			title: 'Large Self Storage Size Chart',
			noPaddingTop: true,
			modules: sizeChartContent,
		},
		{
			title: 'How to Use Large Self Storage',
			pageType: pageTypes.SGP,
			noPadding: true,
			modules: [
				{
					type: 'sgpHeader',
					props: {
						title: 'What About Units Larger Than 10x30?',
						description:
							"For storage needs that go beyond a 10x30, some self storage facilities offer specialty units — including 10x40s, oversized vehicle bays, and climate-controlled warehouse-style spaces. [These are ideal for RVs](https://www.selfstorage.com/blog/long-term-rv-storage-tips-2/), large boats, commercial equipment, and full business inventory. Availability varies by location, so use the SelfStorage.com comparison tool to see what's offered near you.",
						image: headerImage,
					},
				},
			],
		},
		{
			title: 'Large Self Storage Pricing',
			pageType: pageTypes.SGP,
			noPadding: true,
			modules: pricingContent,
		},
		{
			title: 'Choosing the Right Large Self Storage Unit',
			pageType: pageTypes.SGP,
			noPadding: true,
			modules: choosingRightUnitContent,
		},
		{
			title: 'How to Measure for Self Storage',
			background: 'bgImage',
			pageType: pageTypes.SGP,
			modules: howToMeasureContent,
		},
		{
			title: 'Real-Life Measuring Examples',
			noPadding: true,
			modules: realLifeMeasuringContent,
		},
		{
			title: 'Reviews Section',
			background: 'lighterBlue',
			pageType: pageTypes.SGP,
			modules: [
				{
					type: 'reviews',
					title: 'Reviews Section',
					slug: 'large-self-storage-reviews',
					props: {
						pageType: pageTypes.SGP,
						reviewsData: reviewsContent,
					},
				},
			],
		},
		{
			title: 'Faqs Section',
			modules: [
				{
					type: 'faqs',
					props: {
						content: {
							header: 'Frequently Asked Questions',
							description: '',
							faqs: [
								{
									question:
										'How big is a large self storage unit?',
									answer: "Large self storage units are most commonly 10x20 feet — about the size of a standard one-car garage. That's 200 square feet of floor space, with room to store a significant amount of furniture, boxes, and larger items. Some facilities also offer 10x25 or 10x30 units for even bigger storage needs, like full household moves or business inventory.",
								},
								{
									question:
										'What fits in a 10x20 storage unit?',
									answer: "A 10x20 unit can handle the contents of a two- to three-bedroom home, including major furniture pieces. Think multiple bedroom sets, a full living and dining room, appliances, and plenty of boxes. It's a solid choice for a full household move, an extended relocation, or storing the contents of a home during a renovation or estate transition.",
								},
								{
									question:
										'Can you fit a car in a large storage unit?',
									answer: "Yes — a 10x20 is typically large enough to fit a standard sedan, with some room to spare for additional items alongside it. If you're storing a larger vehicle like a truck, SUV, or van, a 10x25 or 10x30 unit will give you more comfortable clearance. Always confirm the unit's exact dimensions with the facility before your move-in date.",
								},
								{
									question:
										'Is a large storage unit big enough for a whole house?',
									answer: 'It depends on the size of your home and how much furniture you have. A 10x20 unit works well for most two- to three-bedroom homes. Larger homes — or households with oversized furniture, multiple appliances, or significant inventory — may need a 10x25 or 10x30 unit. A good rule of thumb: list out your largest items first and use those as your sizing anchor.',
								},
								{
									question:
										'How much does a large self storage unit cost?',
									answer: "Large units are the most spacious option, so they do come with a higher monthly rate — but they can be one of the most cost-effective choices when you're moving or storing a full household. Prices vary by location, facility features, and whether you add climate control. Comparing a few local facilities and checking for move-in promotions can help you find a price that works for your budget.",
								},
							],
						},
					},
				},
			],
		},
		{
			title: 'Related Content',
			background: 'darkBlue',
			modules: [
				{
					type: 'featuredContent',
					props: {
						data: relatedContent,
					},
				},
			],
		},
		linkToOtherPSPsBlock,
		{
			title: 'Storage Near You',
			modules: [
				{
					type: 'storageNearYou',
					props: {
						type: 'StorageSize',
					},
				},
			],
		},
	],
};
